import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell, BarChart, LabelList, Sector,
} from 'recharts';
import { getDashboardInsights, getMonthDaily, getMonthCompare } from '../services/dashboardApi';
import * as orderApi from '../services/orderAnalysisApi';
import * as stockApi from '../services/stockAnalysisApi';
import Card from './Card';
import ReactivatedCard from './ReactivatedCard';
import CustomersDispatchedCard from './CustomersDispatchedCard';
import CustomerHistoryModal from './CustomerHistoryModal';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PALETTE = ['#0E6F8A', '#B25C3F', '#3E72B0', '#237246', '#8F5F00', '#7A5C9E', '#5F6872', '#C0453B'];
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const tick = { fill: 'var(--color-text-secondary)', fontSize: 11 };

function useCountUp(target, ms = 700) {
  const [v, setV] = useState(0);
  useEffect(() => {
    const t = Number(target) || 0;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - start) / ms);
      setV(Math.round(t * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

// SPARK-HOVER v1
function Spark({ data, color }) {
  const [hv, setHv] = useState(null);
  if (!data || data.length < 2) return null;
  const n = data.length;
  const max = Math.max(1, ...data);
  const yOf = (v) => 27 - (v / max) * 24;
  const pts = data.map((v, i) => `${(i / (n - 1)) * 100},${yOf(v)}`);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const cx = e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX;
    const i = Math.round(((cx - r.left) / Math.max(1, r.width)) * (n - 1));
    setHv(Math.max(0, Math.min(n - 1, i)));
  };
  let label = '';
  if (hv != null && n === 30) {
    const d = new Date();
    d.setDate(d.getDate() - (n - 1 - hv));
    label = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }
  const pct = hv == null ? 0 : (hv / (n - 1)) * 100;
  return (
    <div
      style={{ position: 'relative', height: 30, cursor: 'pointer', touchAction: 'pan-y' }}
      onMouseMove={onMove}
      onMouseLeave={() => setHv(null)}
      onTouchStart={onMove}
      onTouchMove={onMove}
      onTouchEnd={() => setHv(null)}
    >
      <svg viewBox="0 0 100 28" preserveAspectRatio="none" style={{ width: '100%', height: 30, display: 'block' }} aria-hidden="true">
        <polygon points={`0,28 ${pts.join(' ')} 100,28`} fill={color} opacity="0.12" />
        <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
        {hv != null && <line x1={pct} x2={pct} y1="0" y2="28" stroke={color} strokeWidth="1" opacity="0.5" vectorEffect="non-scaling-stroke" />}
      </svg>
      {hv != null && (
        <>
          <span style={{ position: 'absolute', left: `${pct}%`, top: `${(yOf(data[hv]) / 28) * 30}px`, width: 8, height: 8, marginLeft: -4, marginTop: -4, borderRadius: '50%', background: color, border: '2px solid var(--color-surface)', pointerEvents: 'none' }} />
          <div
            style={{
              position: 'absolute', bottom: '100%', marginBottom: 4, left: `${pct}%`,
              transform: `translateX(${pct < 18 ? '0%' : pct > 82 ? '-100%' : '-50%'})`,
              background: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)',
              borderRadius: 8, padding: '3px 8px', fontSize: 12, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 20,
              boxShadow: '0 4px 14px rgba(0,0,0,.18)',
            }}
          >
            {label && <span style={{ color: 'var(--color-text-muted)' }}>{label}: </span>}
            <b>{Number(data[hv] || 0).toLocaleString('en-IN')}</b> box
          </div>
        </>
      )}
    </div>
  );
}

function Delta({ cur, prev }) {
  if (!prev || prev <= 0) return <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>no data for last month</span>;
  const pct = ((cur - prev) / prev) * 100;
  const up = pct >= 0;
  return (
    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: up ? 'var(--color-success)' : 'var(--color-danger)' }}>
      {up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
      <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}> vs same days last month</span>
    </span>
  );
}

function HeroKpi({ label, value, tone = 'accent', cur, prev, spark, sub, onClick }) {
  const shown = useCountUp(value);
  const color = `var(--color-${tone})`;
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{
        flex: '1 1 220px', minWidth: 200, background: 'var(--color-surface)', border: '1px solid var(--color-border)',
        borderTop: `4px solid ${color}`, borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-card)',
        padding: 'var(--space-3) var(--space-4)', cursor: onClick ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', gap: 4,
      }}
    >
      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>{label}</div>
      <div className="data-figure" style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-2xl)', fontWeight: 700, color, lineHeight: 1.1 }}>
        {fmt(shown)}
      </div>
      {cur !== undefined && <Delta cur={cur} prev={prev} />}
      {sub && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{sub}</div>}
      {spark && <Spark data={spark} color={color} />}
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        {[0, 1, 2, 3].map((i) => <div key={i} className="skel" style={{ flex: '1 1 220px', height: 120 }} />)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--space-4)' }}>
        {[0, 1, 2].map((i) => <div key={i} className="skel" style={{ height: 260 }} />)}
      </div>
    </div>
  );
}

// MONTH-DAILY v1
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const ttStyle = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 };

function MonthDailyModal({ ym, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const navigate = useNavigate();
  useEffect(() => {
    let alive = true;
    setData(null); setErr('');
    getMonthDaily(ym).then((d) => { if (alive) setData(d); }).catch(() => { if (alive) setErr('Could not load day-wise data.'); });
    return () => { alive = false; };
  }, [ym]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const [y, m] = ym.split('-').map(Number);
  const rows = (data?.days || []).map((d) => ({ label: String(d.day), Dispatch: d.dispatch, Production: d.production }));
  const totD = rows.reduce((a, r) => a + (r.Dispatch || 0), 0);
  const totP = rows.reduce((a, r) => a + (r.Production || 0), 0);
  const showProd = Boolean(data?.hasProduction);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${MONTH_NAMES[m - 1]} ${y} day-wise`}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-surface)', color: 'var(--color-text)', borderRadius: 14, width: 'min(1000px, 100%)', maxHeight: '92vh', overflow: 'auto', padding: 'var(--space-4)', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg, 18px)' }}>
            {MONTH_NAMES[m - 1]} {y} — day-wise {showProd ? 'Production vs Dispatch' : 'Dispatch'}
          </h2>
          <span style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-primary" onClick={() => navigate(`/ref-party-month?month=${ym}`)}>Full details</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </span>
        </div>
        {err && <p style={{ color: 'var(--color-danger)' }}>{err}</p>}
        {!data && !err && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}
        {data && (
          <>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8, fontSize: 'var(--font-size-sm)' }}>
              <span><b style={{ color: '#0E6F8A' }}>Dispatch:</b> {fmt(totD)} box</span>
              {showProd && <span><b style={{ color: '#B25C3F' }}>Production:</b> {fmt(totP)} box</span>}
            </div>
            <div style={{ width: '100%', height: 340 }}>
              <ResponsiveContainer>
                <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                  <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={{ stroke: 'var(--color-border)' }} interval={0} />
                  <YAxis tick={tick} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                  <Tooltip formatter={(v) => fmt(v)} labelFormatter={(l) => `${l} ${MONTH_NAMES[m - 1]} ${y}`} contentStyle={ttStyle} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Dispatch" fill="#0E6F8A" radius={[3, 3, 0, 0]} maxBarSize={18} />
                  {showProd && <Line type="monotone" dataKey="Production" stroke="#B25C3F" strokeWidth={2.2} dot={{ r: 2.5 }} />}{/* MONTH-DAILY LINE v1 */}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// LOOK5 v1: donut whose hovered slice pops out (rounded slice, lifted with a soft shadow)
// SIZE-CLICK v1: click a size -> /ref-party-month opens that size's customer / design / batch detail
function SizeDonutChart({ width, height, sizes, total, month }) {
  const navigate = useNavigate();
  const [hv, setHv] = useState(null);
  const openSize = (d) => {
    if (!d || !d.name) return;
    const n = new Date();
    const ym = month || `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
    navigate(`/ref-party-month?month=${ym}&size=${encodeURIComponent(d.name)}`);
  };
  const lift = (p) => (
    <Sector
      cx={p.cx} cy={p.cy} innerRadius={p.innerRadius - 2} outerRadius={p.outerRadius + 12}
      startAngle={p.startAngle} endAngle={p.endAngle} fill={p.fill} cornerRadius={8}
      style={{ filter: 'drop-shadow(0 8px 10px rgba(60, 60, 80, 0.30))', transition: 'all .18s ease' }}
    />
  );
  return (
    <PieChart width={width} height={height}>
      <Pie
        data={sizes} dataKey="boxes" nameKey="name" innerRadius="50%" outerRadius="76%" paddingAngle={3} cornerRadius={8}
        stroke="none" activeIndex={hv} activeShape={lift} cursor="pointer" onClick={(d) => openSize(d)}
        onMouseEnter={(_, i) => setHv(i)} onMouseLeave={() => setHv(null)}
      >
        {sizes.map((s, i) => <Cell key={s.name} fill={PALETTE[i % PALETTE.length]} />)}
      </Pie>
      <Tooltip formatter={(v, n) => [`${fmt(v)} (${((v / total) * 100).toFixed(1)}%)`, n]} contentStyle={{ borderRadius: 10, fontSize: 12 }} />
      <Legend wrapperStyle={{ fontSize: 12 }} />
    </PieChart>
  );
}

// REVERT-COLORS v1
function MonthChartCard({ ins, monthData }) {
  const navigate = useNavigate();
  const [ym, setYm] = useState(null);
  const open = (idx) => {
    const r = ins.monthly && ins.monthly[idx];
    if (!r || !r.ym) return;
    if (ins.restricted) navigate(`/ref-party-month?month=${r.ym}`);
    else setYm(r.ym);
  };
  return (
    <>
      <Card title={ins.hasProduction ? 'Dispatch vs Production (12 months)' : 'Monthly Dispatch (12 months)'} style={{ marginBottom: 0 }}>
        <div style={{ width: '100%', height: 270, cursor: 'pointer' }}>
          <ResponsiveContainer>
            <ComposedChart
              data={monthData}
              margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
              onClick={(st) => { if (st && st.activeTooltipIndex != null) open(Number(st.activeTooltipIndex)); }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={{ stroke: 'var(--color-border)' }} interval="preserveStartEnd" />
              <YAxis tick={tick} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
              <Tooltip formatter={(v) => fmt(v)} contentStyle={ttStyle} />
              {ins.hasProduction && <Legend wrapperStyle={{ fontSize: 12 }} />}
              <Bar dataKey="Dispatch" fill="#0E6F8A" radius={[4, 4, 0, 0]} maxBarSize={28} />
              {ins.hasProduction && <Line type="monotone" dataKey="Production" stroke="#B25C3F" strokeWidth={2.2} dot={{ r: 3 }} />}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Click a month to see the day-wise chart.</div>
      </Card>
      {ym && <MonthDailyModal ym={ym} onClose={() => setYm(null)} />}
    </>
  );
}


// MONTH-COMPARE v1: Previous / Next month buttons + comparison with the month before, for the Size-wise and Ref. Party-wise cards.
const mcLabel = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;

function useMonthCompare() {
  const [month, setMonth] = useState('');
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    setD(null); setErr(false);
    getMonthCompare(month || undefined).then((x) => { if (alive) setD(x); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [month]);
  const shift = (delta) => {
    if (!d) return;
    const dt = new Date(Date.UTC(Number(d.ym.slice(0, 4)), Number(d.ym.slice(5, 7)) - 1 + delta, 1));
    setMonth(`${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`);
  };
  return { d, err, shift, month, setMonth };
}

function prevLabel(d) {
  return `${mcLabel(d.prevYm)}${d.isCurrent ? ` (1–${d.upTo})` : ''}`;
}

function MonthNav({ d, shift, month, setMonth }) {
  const B = { border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-primary)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', cursor: 'pointer', font: 'inherit', fontSize: 'var(--font-size-xs)' };
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        <strong style={{ color: 'var(--color-text-primary)' }}>{mcLabel(d.ym)}{d.isCurrent ? ` (1–${d.upTo})` : ''}</strong> compared with {prevLabel(d)}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {month && <button type="button" style={B} onClick={() => setMonth('')}>This month</button>}
        <button type="button" style={B} onClick={() => shift(-1)}>◀ Previous</button>
        <button type="button" style={B} onClick={() => shift(1)} disabled={d.isCurrent}>Next ▶</button>
      </div>
    </div>
  );
}

function ChangeTag({ cur, prev }) {
  if (prev > 0) {
    const p = ((cur - prev) / prev) * 100;
    return <span style={{ color: p >= 0 ? 'var(--color-success)' : 'var(--color-danger)', fontWeight: 700 }}>{p >= 0 ? '▲' : '▼'} {Math.abs(p).toFixed(1)}%</span>;
  }
  return cur > 0 ? <span style={{ color: 'var(--color-success)', fontWeight: 700 }}>New</span> : <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
}

function CompareTable({ rows, d, first, onRow, maxHeight = 220 }) {
  const th = { textAlign: 'left', padding: '6px 8px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
  const td = { padding: '6px 8px', fontSize: 'var(--font-size-sm)', borderBottom: '1px solid var(--color-border)' };
  const R = { textAlign: 'right' };
  const tc = rows.reduce((t, r) => t + r.cur, 0);
  const tp = rows.reduce((t, r) => t + r.prev, 0);
  return (
    <div style={{ overflow: 'auto', maxHeight, marginTop: 'var(--space-2)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr><th style={th}>{first}</th><th style={{ ...th, ...R }}>{mcLabel(d.ym)}</th><th style={{ ...th, ...R }}>{prevLabel(d)}</th><th style={{ ...th, ...R }}>Change</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} style={onRow ? { cursor: 'pointer' } : undefined} onClick={onRow ? () => onRow(r) : undefined}>
              <td style={{ ...td, fontWeight: 600 }}>{r.name}</td>
              <td style={{ ...td, ...R, fontWeight: 700 }}>{fmt(r.cur)}</td>
              <td style={{ ...td, ...R }}>{fmt(r.prev)}</td>
              <td style={{ ...td, ...R }}><ChangeTag cur={r.cur} prev={r.prev} /></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={{ ...td, fontWeight: 800 }}>Total</td>
            <td style={{ ...td, ...R, fontWeight: 800 }}>{fmt(tc)}</td>
            <td style={{ ...td, ...R, fontWeight: 800 }}>{fmt(tp)}</td>
            <td style={{ ...td, ...R }}><ChangeTag cur={tc} prev={tp} /></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function SizeWiseCard() {
  const { d, err, shift, month, setMonth } = useMonthCompare();
  const sizes = d ? d.sizes.filter((x) => x.cur > 0).map((x) => ({ name: x.name, boxes: x.cur })) : [];
  const total = sizes.reduce((t, x) => t + x.boxes, 0);
  return (
    <Card title={`Size-wise Dispatch${d ? ` — ${mcLabel(d.ym)}` : ' (this month)'}`} style={{ marginBottom: 0 }}>
      {err && <p style={{ color: 'var(--color-danger)', margin: 0 }}>Could not load.</p>}
      {!d && !err && <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Loading…</p>}
      {d && (
        <>
          <MonthNav d={d} shift={shift} month={month} setMonth={setMonth} />
          {/* SIZE-LAYOUT v1 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-4)', alignItems: 'center' }}>
            <div>
              {sizes.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No dispatch in this month{d.isCurrent ? ' yet' : ''}.</p>
              ) : (
                <div style={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <SizeDonutChart sizes={sizes} total={total} month={d.ym} />
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <CompareTable rows={d.sizes} d={d} first="Size" maxHeight={320} />
          </div>
        </>
      )}
    </Card>
  );
}

function RefPartyWiseCard({ setActive }) {
  const { d, err, shift, month, setMonth } = useMonthCompare();
  const rows = d ? d.refParties : [];
  const bars = rows.filter((r) => r.cur > 0).map((r) => ({ id: r.id, name: r.name, boxes: r.cur, prev: r.prev, customers: r.customers }));
  const total = bars.reduce((t, r) => t + r.boxes, 0) || 1;
  const lastDay = d ? new Date(Number(d.ym.slice(0, 4)), Number(d.ym.slice(5, 7)), 0).getDate() : 0;
  const open = (r) => setActive({ id: r.id, name: r.name, kind: 'refParty', from: `${d.ym}-01`, to: `${d.ym}-${String(lastDay).padStart(2, '0')}` });
  const trim = (n) => (String(n).length > 17 ? `${String(n).slice(0, 16)}…` : n);
  return (
    <Card title={`Ref. Party-wise Sales${d ? ` — ${mcLabel(d.ym)}` : ' (this month)'}`} style={{ marginTop: 'var(--space-4)', marginBottom: 0 }}>
      {err && <p style={{ color: 'var(--color-danger)', margin: 0 }}>Could not load.</p>}
      {!d && !err && <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Loading…</p>}
      {d && (
        <>
          <MonthNav d={d} shift={shift} month={month} setMonth={setMonth} />
          {bars.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No dispatch in this month{d.isCurrent ? ' yet' : ''}.</p>
          ) : (
            <>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
                {bars.length} Ref. Parties · <strong style={{ color: 'var(--color-text-primary)' }}>{fmt(total)}</strong> box in total
              </div>
              <div style={{ width: '100%', height: Math.max(220, bars.length * 30 + 30) }}>
                <ResponsiveContainer>
                  <BarChart data={bars} layout="vertical" margin={{ top: 0, right: 56, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                    <XAxis type="number" tick={tick} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                    <YAxis type="category" dataKey="name" width={130} tick={tick} tickLine={false} axisLine={false} interval={0} tickFormatter={trim} />
                    <Tooltip
                      formatter={(v, n, p) => [`${fmt(v)} box (${((v / total) * 100).toFixed(1)}%) · ${p.payload.customers} customers · ${mcLabel(d.prevYm)}: ${fmt(p.payload.prev)}`, 'Sales']}
                      contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }}
                    />
                    <Bar dataKey="boxes" radius={[0, 4, 4, 0]} maxBarSize={20} cursor="pointer" onClick={(r) => open(r)}>
                      {bars.map((r, i) => <Cell key={r.id} fill={PALETTE[i % PALETTE.length]} />)}
                      <LabelList dataKey="boxes" position="right" formatter={fmt} style={{ fill: 'var(--color-text-secondary)', fontSize: 11 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Click a bar or a row to open that Ref. Party&apos;s report for this month.</div>
            </>
          )}
          <CompareTable rows={rows} d={d} first="Ref. Party" onRow={open} />
        </>
      )}
    </Card>
  );
}

export default function DashboardInsights({ restricted, onOpenMonthDispatch }) {
  const navigate = useNavigate();
  const [ins, setIns] = useState(null);
  const [pending, setPending] = useState(null);
  const [freeStock, setFreeStock] = useState(null);
  const [updated, setUpdated] = useState(null);
  const [spin, setSpin] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    setSpin(true);
    const jobs = [
      getDashboardInsights().then(setIns).catch(() => {}),
      orderApi.getPendingOrderGroups().then((g) => setPending(g.length)).catch(() => {}),
    ];
    if (!restricted) {
      jobs.push(stockApi.getOrderlessStockCards()
        .then((c) => setFreeStock(c.reduce((t, s) => t + Number(s.freeQty != null ? s.freeQty : s.totalQty || 0), 0)))
        .catch(() => {}));
    }
    Promise.all(jobs).then(() => { setUpdated(new Date()); setSpin(false); });
  }, [restricted]);

  useEffect(() => { load(); }, [load]);

  if (!ins) return <DashboardSkeleton />;

  const monthData = ins.monthly.map((m) => ({
    label: `${MONTHS[Number(m.ym.slice(5, 7)) - 1]} ${m.ym.slice(2, 4)}`,
    Dispatch: m.dispatch,
    Production: m.production,
  }));
  const sizeTotal = ins.sizes.reduce((t, s) => t + s.boxes, 0);
  const refTotal = (ins.refParties || []).reduce((t, r) => t + r.boxes, 0) || 1;
  const nowD = new Date();
  const p2 = (n) => String(n).padStart(2, '0');
  const monthFrom = `${nowD.getFullYear()}-${p2(nowD.getMonth() + 1)}-01`;
  const monthTo = `${nowD.getFullYear()}-${p2(nowD.getMonth() + 1)}-${p2(new Date(nowD.getFullYear(), nowD.getMonth() + 1, 0).getDate())}`;
  const trunc = (n) => (String(n).length > 17 ? `${String(n).slice(0, 16)}…` : n);
  void sizeTotal; void refTotal; void monthFrom; void monthTo; // MONTH-COMPARE v1

  return (
    <div style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <div style={{ flex: 1, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          Today: <strong style={{ color: 'var(--color-text-primary)' }}>{fmt(ins.today.customers)}</strong> customers ·{' '}
          <strong style={{ color: 'var(--color-text-primary)' }}>{fmt(ins.today.boxes)}</strong> box dispatched
        </div>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          Updated {updated ? updated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
        </span>
        <button
          type="button"
          onClick={load}
          disabled={spin}
          style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-primary)', borderRadius: 'var(--radius-sm)', padding: '5px 12px', cursor: 'pointer', font: 'inherit', fontSize: 'var(--font-size-xs)' }}
        >
          {spin ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {/* GROWTH-LINKS v1 */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        {[['reorder', 'Reorder Due'], ['winback', 'Win-back'], ['target', 'Target vs Achievement'], ['trend', 'Design / Size Trend']].map(([k, label]) => (
          <button key={k} type="button" className="btn btn-secondary" onClick={() => navigate(`/growth?tab=${k}`)}>{label}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        <HeroKpi
          label="Dispatched This Month"
          value={ins.mtd.boxes}
          cur={ins.mtd.boxes}
          prev={ins.prevMtd.boxes}
          spark={ins.spark}
          sub="box · last 30 days trend"
          onClick={onOpenMonthDispatch}
        />
        <CustomersDispatchedCard
          Kpi={(p) => (
            <HeroKpi
              label={p.label}
              value={p.value}
              tone="info"
              cur={ins.mtd.customers}
              prev={ins.prevMtd.customers}
              sub="click for the list"
            />
          )}
        />
        <HeroKpi
          label="Pending Orders"
          value={pending || 0}
          tone="warning"
          sub="orders waiting for production / dispatch"
          onClick={() => navigate('/order-analysis', { state: { focus: 'pending' } })}
        />
        {!restricted && (
          <HeroKpi
            label="Free Orderless Stock"
            value={freeStock || 0}
            tone="success"
            sub="box available (without HOLD)"
            onClick={() => navigate('/stock-analysis')}
          />
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--space-4)' }}>
        <MonthChartCard ins={ins} monthData={monthData} />


        <Card title="Top 10 Customers (this year)" style={{ marginBottom: 0 }}>
          {ins.topCustomers.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No dispatch yet this year.</p>
          ) : (
            <div style={{ width: '100%', height: 270 }}>
              <ResponsiveContainer>
                <BarChart data={ins.topCustomers} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                  <XAxis type="number" tick={tick} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                  <YAxis type="category" dataKey="name" width={120} tick={tick} tickLine={false} axisLine={false} tickFormatter={trunc} />
                  <Tooltip formatter={(v) => [fmt(v), 'Box']} contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="boxes" fill="#3E72B0" radius={[0, 4, 4, 0]} maxBarSize={18} cursor="pointer" onClick={(d) => setActive({ id: d.id, name: d.name })} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {ins.topCustomers.length > 0 && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Click a bar to open the customer report.</div>}
        </Card>
      </div>

      <div style={{ marginTop: 'var(--space-4)' }}><SizeWiseCard /></div>

      <div style={{ marginTop: 'var(--space-4)' }}><ReactivatedCard /></div>

      {!restricted && ins.isAdmin && <RefPartyWiseCard setActive={setActive} />}
      {active && <CustomerHistoryModal customer={active} onClose={() => setActive(null)} />}
    </div>
  );
}
// REFPARTY-REPORT v1
// REFPARTY-OLD v1
